import type { Design } from '@/infra/design'
import {
  darkenUntil,
  getApcaContrast,
  getContrastRatio,
} from '@/utils/color-util'

/**
 * The theme colors in effect, after adjusting the brand for readability.
 */
export interface EffectiveTheme {
  /**
   * Brand color for surfaces that carry white labels, such as the nav.
   */
  brand: string

  /**
   * Accent color, used for most things including text on white.
   */
  accent: string
}

/**
 * Lightness contrast the nav's white labels need on the brand color.
 * Lc 70 suits its 13px medium text; WCAG 2's 4.5:1 darkens mid-tone brands
 * far more than legibility needs, which visibly muddies them.
 */
const brandLabelContrast = 70

/**
 * Contrast accent text, such as links, needs on the white content card.
 */
const accentTextContrast = 4.5

/**
 * Adjusts the workspace's brand color where it would be unreadable. The
 * classic design uses it as is.
 *
 * @param brand
 * @param design
 */
export function toEffectiveTheme(
  brand: string,
  design: Design,
): EffectiveTheme {
  if (design === 'classic') {
    return { brand, accent: brand }
  }

  return {
    brand: darkenUntil(
      brand,
      (color) => getApcaContrast('#fff', color) >= brandLabelContrast,
    ),
    accent: darkenUntil(
      brand,
      (color) => getContrastRatio(color, '#fff') >= accentTextContrast,
    ),
  }
}
